/**
 * exercise-row-handler.js
 */

import React from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { getAttendance } from 'utils/event-attendance';
import { handlerExerciseCompletionStatus } from 'utils/record-status';
import useRecordStore from 'contexts/record-store';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import IconPatrolDetection from 'components/icon-patrol-detection';
import AnswerIcon from 'components/training/answer/icon';
import CompletionStatusIcon from 'components/record-status-icons/completion';
import ReviewStatusIconExerciseHandler from 'components/record-status-icons/review/review-status-icon-exercise-handler';
import {
  getOdorSummary,
  getPatrolSummary,
  getReviewStatusObj,
} from '../helpers';
import Row from '../row/row';
import RowStatusChip from '../row/row-status-chip';
import RowStatusTooltip from '../row/row-status-tooltip';


const useStyles = makeStyles(theme => ({
  completionChip: {
    minWidth: 110,
  },
}));


export default function ExerciseRowHandler(props) {
  const {
    id,
    exercise,
    event,
    basePath,
    isSelected,
    onSelectionChange,
    isFuture,
    workMode,
    workModeName,
    onViewReport,
    isRecordTrackingRelevant
  } = props;
  const classes = useStyles();
  const { t } = useTranslation('general');
  const { user } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const recordContextMenu = React.useContext(RecordMenuContext);
  const isOptional = event.membership?.isOptional;
  const isAttending = event.membership?.isAttending;
  const attendance = getAttendance(isOptional, isAttending);
  const isReportDisabled = !exercise.appliesToHandler || exercise.userStats.reportableAnswerIds.length === 0;
  const reportDisabledReason = !exercise.appliesToHandler
    ? 'This exercise does not apply to any of your dogs'
    : exercise.userStats.reportableAnswerIds.length === 0
      ? 'This exercise has no printable records'
      : null;
  const completionStatusParams = React.useMemo(() => {
    return handlerExerciseCompletionStatus(
      exercise.userStats.complete,
      exercise.userStats.incomplete,
      exercise.userStats.outdated,
      event,
      null,
      exercise.answers.length
    );
    // event is hookstate obj
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    exercise.userStats.complete,
    exercise.userStats.incomplete,
    exercise.userStats.outdated,
    exercise.answers.length,
    event.date,
    isOptional,
    isAttending
  ]);


  // completion / answers
  const answerRecords = recordStore.useRecordArray(Types.Record.ANSWER, exercise.answers);
  // efficiently get answer collection fields
  const {
    answerToEdit,
    hasOwnCompletions,
    numDogsDone,
    numDogsReviewed,
    numDogsRejected,
    numDogsOutdated
  } = (() => {
    let answerToEdit = null;
    let answerToEditPriority = 99;
    let hasOwnCompletions = false;
    let numDogsDone = 0;
    let numDogsReviewed = 0;
    let numDogsRejected = 0;
    let numDogsOutdated = 0;

    for (let i = 0; i < answerRecords.length; i++) {
      const a = answerRecords[i].get();

      // we automatically open an exercise with the completion editor for a number of reasons.
      // in a collection of answers (dogs), we need to use a priority hierarchy to rank the most
      // important answer so that we use the top ranking answer to open. 
      // as an example, we should open to an incomplete answer before opening to a rejected or
      // outdated answer. the following is the priority order:
      // incomplete = 1
      // rejected = 2
      // outdated = 3

      // incomplete test
      if (!isFuture
        && attendance.id === Types.Attendance.ATTENDING
        && a.dog.appliesToExercise
        && !a.dateCompleted
        && (answerToEditPriority > 1)
      ) {
        answerToEdit = a;
        answerToEditPriority = 1;
      }
      // rejected test 
      else if (a.review?.status === Types.ReviewStatus.REJECTED
        && answerToEditPriority > 2
      ) {
        answerToEdit = a;
        answerToEditPriority = 2;
      }
      // outdated test 
      else if (a.isOutdated
        && answerToEditPriority > 3
      ) {
        answerToEdit = a;
        answerToEditPriority = 3;
      }

      // has own completions
      if (!hasOwnCompletions && a.dog.appliesToExercise && a.dateCompleted) {
        hasOwnCompletions = true;
      }

      // num dogs done
      if (!a.dog.appliesToExercise || a.dateCompleted) {
        numDogsDone++;
      }

      // reviewed / rejected
      if (a.review?.status === Types.ReviewStatus.ACCEPTED) {
        numDogsReviewed++;
      }
      else if (a.review?.status === Types.ReviewStatus.REJECTED) {
        numDogsRejected++;
      }

      // outdated
      if (a.isOutdated) {
        numDogsOutdated++;
      }
    }

    return {
      answerToEdit,
      hasOwnCompletions,
      numDogsDone,
      numDogsReviewed,
      numDogsRejected,
      numDogsOutdated
    };
  })();


  const customCompare = answerRecords.length === 0
    ? ''
    : attendance.id === Types.Attendance.ATTENDING
      ? `complete:${numDogsDone}-rv:${numDogsReviewed}-rj:${numDogsRejected}-od:${numDogsOutdated}-irtr:${isRecordTrackingRelevant}`
      : `unknown-attendance`;


  // route link

  // TODO: i think this logic should be in the exercise container and redirect to the appropriate place based on state,
  // consistent with how deployments has been done. it would simplify the rendering and push the dynamic destination
  // logic instead to the opening action which would be more efficient as it would only run on record open.

  // the primary challenge with this is that mobile doesn't have a dedicated exercise/completion routing page
  // like desktop does (exercise-container). 
  // NOTE: this may not be true anymore...

  const to = makeLocation(
    basePath,
    'training',
    answerToEdit && user.role.status.isActive ? 'editAnswer' : 'viewExercise',
    answerToEdit && user.role.status.isActive ? { exerciseId: id, answerId: answerToEdit.id } : { exerciseId: id }
  );
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;


  const getCompletionStatusLabel = (answer) => {
    if (answer.isOutdated) {
      return 'Outdated';
    } else if (answer.dateCompleted) {
      return 'Complete';
    } else if (!answer.dog.appliesToExercise) {
      return 'Not Applicable';
    } else if (attendance.id !== Types.Attendance.ATTENDING) {
      return 'Applicable';
    } else if (isFuture) {
      return 'Upcoming';
    } else {
      return 'Incomplete';
    }
  }


  const completionStatusTooltipItems = answerRecords.map(answer => ({
    id: answer.get().id,
    icon: <AnswerIcon answer={answer.get()} membership={event.membership} />,
    name: answer.get().dog.name,
    status: getCompletionStatusLabel(answer.get())
  }));


  const reviewStatusTooltipItems = answerRecords
    .filter(answer => Boolean(answer.get().dateCompleted))
    .map(answer => getReviewStatusObj(answer.get().id, answer.get().dog.name, answer.get().review));


  // menu items
  const menuItems = [
    // view
    {
      title: 'View',
      iconType: 'view',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'viewExercise', { exerciseId: id }, null),
      isDefaultItem: !user.role.status.isActive || !Boolean(answerToEdit)
    },

    // edit
    {
      title: 'Edit',
      iconType: 'edit',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'editExercise', { exerciseId: id }, null),
      disabled: !user.role.status.isActive || !exercise.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit this exercise'
    },

    // complete exercise
    {
      title: 'Complete',
      iconType: 'complete-outlined',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'editAnswer', { exerciseId: id, answerId: answerToEdit?.id }, null),
      isDefaultItem: Boolean(answerToEdit),
      disabled: !user.role.status.isActive || exercise.isComplete || !Boolean(answerToEdit),
      disabledReason: !user.role.status.isActive
        ? t('readOnlyAccount')
        : exercise.isComplete
          ? 'This exercise has already been completed'
          : isFuture
            ? 'This event is in the future'
            : attendance.id !== Types.Attendance.ATTENDING
              ? <>You cannot complete exercises until<br />you mark the event as attending</>
              : !exercise.appliesToHandler
                ? 'This exercise does not apply to any of your dogs'
                : ''
    },

    // view report
    {
      title: 'View Report',
      iconType: 'report',
      onSelect: () => onViewReport(Types.RecordGroup.TRAINING, exercise.userStats.reportableAnswerIds),
      disabled: isReportDisabled,
      disabledReason: reportDisabledReason,
      tooltip: exercise.userStats.outdated > 0
        ? `${exercise.userStats.outdated} outdated record${exercise.userStats.outdated === 1 ? '' : 's'} will not be included in the report`
        : ''
    },

    // move to event
    {
      title: 'Move to Event ...',
      iconType: 'transfer',
      onSelect: () => recordContextMenu.moveToEventAction(id, exercise.eventId),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to move this exercise'
    },

    // duplicate submenu
    {
      title: 'Duplicate ...',
      iconType: 'copy',
      onSelect: () => recordContextMenu.duplicateExercise(id),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit the containing event'
    },

    // delete submenu
    {
      title: 'Delete',
      iconType: 'delete',
      disabled: !user.role.status.isActive || isRecordTrackingRelevant,
      disabledReason: !user.role.status.isActive
        ? t('readOnlyAccount')
        : isRecordTrackingRelevant
          ? 'Tracking is active for this exercise'
          : '',
      submenuItems: [
        {
          title: 'K9 Completion ...',
          iconType: 'complete-outlined',
          onSelect: () => recordContextMenu.deleteCompletedAnswers(id),
          disabled: !user.role.status.isActive || !hasOwnCompletions,
          disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You have no completions for this exercise'
        },
        {
          title: 'Exercise ...',
          iconType: 'exercise',
          onSelect: () => recordContextMenu.deleteExercise(id),
          disabled: !user.role.status.isActive || !exercise.canEdit || exercise.fullStats.complete > 0,
          disabledReason: !user.role.status.isActive
            ? t('readOnlyAccount')
            : !exercise.canEdit
              ? 'You do not have permission to delete this exercise'
              : <>
                Exercises with completions cannot be deleted.
                <Box mt={0.5}>
                  Members with completions:
                  <ul style={{ listStyleType: 'none', marginTop: 0 }}>
                    {exercise.applicableHandlerDogs
                      .filter(handler => handler.hasCompletions)
                      .map(handler => <li key={handler.id}>{handler.name}</li>)}
                  </ul>
                </Box>
              </>
        }
      ]
    }
  ];


  return (
    <>
      {exercise &&
        <Row
          data-testid={id}
          data-testtype='record-exercise'
          recordType={Types.Record.EXERCISE}
          className='content'
          routeLink={routeLink}

          selection={{
            type: Types.RecordGroup.TRAINING,
            id,
            isSelected,
            isDisabled: isReportDisabled,
            disabledReason: reportDisabledReason,
            onChange: onSelectionChange
          }}

          icon={<IconPatrolDetection
            workMode={exercise.workMode}
            patrolType={workMode === Types.WorkMode.PATROL && exercise.patrol.patrolTypes.length === 1
              ? exercise.patrol.patrolTypes[0].type
              : Types.Patrol.SCENARIO}
          />}
          title={exercise.name}
          detail={`${workModeName}: ${workMode === Types.WorkMode.PATROL
            ? getPatrolSummary(Types.Record.EXERCISE, exercise.patrol.patrolTypes, t)
            : getOdorSummary(exercise.detection.environments)}`}

          optional={
            <div className='flex-hc record-status'>
              {/* review icon */}
              <div className='review-status'>
                <RowStatusTooltip items={reviewStatusTooltipItems}>
                  <ReviewStatusIconExerciseHandler
                    reviewStats={exercise.reviewStats}
                    isAvailable={exercise.userStats.complete > 0}
                    to={to}
                  />
                </RowStatusTooltip>
              </div>

              {/* completion chip - desktop */}
              <span className='status-desktop'>
                <RowStatusTooltip items={completionStatusTooltipItems}>
                  <RowStatusChip
                    className={
                      `chip-${completionStatusParams.id}`
                      + ` chip-font-${completionStatusParams.id}`
                      + ` ${classes.completionChip}`}
                    label={<><Icon type={completionStatusParams.id} />{completionStatusParams.label}</>}
                    to={to}
                  />
                </RowStatusTooltip>
              </span>
              {/* completion icon - mobile */}
              <span className='status-mobile'>
                <CompletionStatusIcon
                  params={completionStatusParams}
                  to={to}
                />
              </span>
            </div>}

          menu={{
            ariaLabel: 'exercise menu options',
            className: '',
            items: menuItems
          }}

          customCompare={customCompare}
        />}
    </>
  )
}